import React, { useState, useEffect } from 'react';
import { X, Search, Save } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { TestPrepStudent, TestPrepDocumentService } from '../../../types';

interface AddDocumentServiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    editService?: TestPrepDocumentService;
}

const AddDocumentServiceModal: React.FC<AddDocumentServiceModalProps> = ({ isOpen, onClose, onSave, editService }) => {
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [students, setStudents] = useState<TestPrepStudent[]>([]);
    const [searchResults, setSearchResults] = useState<TestPrepStudent[]>([]);
    const [selectedStudent, setSelectedStudent] = useState<TestPrepStudent | null>(null);

    const [formData, setFormData] = useState<{
        service_type: string[];
        status: string;
        deadline: string;
        notes: string;
        price: string;
        currency: string;
        payment_method: string;
        is_paid: boolean;
        company_entity: string;
    }>({
        service_type: ['cv'],
        status: 'brief_received',
        deadline: '',
        notes: '',
        price: '',
        currency: 'TRY',
        payment_method: '',
        is_paid: false,
        company_entity: ''
    });

    useEffect(() => {
        if (isOpen) {
            if (editService) {
                // If editing, we just need the student info if available or just use the ID
                // Ideally we have the student object populated
                setFormData({
                    service_type: editService.service_type,
                    status: editService.status,
                    deadline: editService.deadline ? new Date(editService.deadline).toISOString().split('T')[0] : '',
                    notes: editService.notes || '',
                    price: editService.price?.toString() || '',
                    currency: editService.currency || 'TRY',
                    payment_method: editService.payment_method || '',
                    is_paid: editService.is_paid || false,
                    company_entity: editService.company_entity || ''
                });
                if (editService.student) {
                    setSelectedStudent({
                        id: editService.student_id,
                        first_name: editService.student.first_name,
                        last_name: editService.student.last_name,
                        status: 'active', // Dummy
                        created_at: '' // Dummy
                    });
                }
            } else {
                // Reset form
                setFormData({
                    service_type: ['cv'],
                    status: 'brief_received',
                    deadline: '',
                    notes: '',
                    price: '',
                    currency: 'TRY',
                    payment_method: '',
                    is_paid: false,
                    company_entity: ''
                });
                setSelectedStudent(null);
                setSearchTerm('');
                fetchStudents();
            }
        }
    }, [isOpen, editService]);

    useEffect(() => {
        if (!selectedStudent && searchTerm) {
            const lower = searchTerm.toLowerCase();
            setSearchResults(students.filter(s =>
                s.first_name.toLowerCase().includes(lower) ||
                s.last_name.toLowerCase().includes(lower)
            ));
        } else {
            setSearchResults([]);
        }
    }, [searchTerm, students, selectedStudent]);

    const fetchStudents = async () => {
        try {
            const { data, error } = await supabase
                .from('testprep_students')
                .select('*')
                .eq('status', 'active')
                .order('first_name');
            if (error) throw error;
            setStudents(data || []);
        } catch (error) {
            console.error('Error fetching students:', error);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedStudent) {
            alert('Lütfen bir öğrenci seçiniz.');
            return;
        }

        setLoading(true);
        try {
            if (editService) {
                const { error } = await supabase
                    .from('testprep_document_services')
                    .update({
                        service_type: formData.service_type,
                        status: formData.status,
                        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null,
                        notes: formData.notes,
                        price: formData.price ? parseFloat(formData.price) : null,
                        currency: formData.currency,
                        payment_method: formData.payment_method || null,
                        is_paid: formData.is_paid,
                        company_entity: formData.company_entity || null,
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', editService.id);
                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('testprep_document_services')
                    .insert({
                        student_id: selectedStudent.id,
                        service_type: formData.service_type,
                        status: formData.status,
                        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null,
                        notes: formData.notes,
                        price: formData.price ? parseFloat(formData.price) : null,
                        currency: formData.currency,
                        payment_method: formData.payment_method || null,
                        is_paid: formData.is_paid,
                        company_entity: formData.company_entity || null
                    });
                if (error) throw error;
            }
            onSave();
            onClose();
        } catch (error) {
            console.error('Error saving document service:', error);
            alert('Kaydedilirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg flex flex-col max-h-[90vh]">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                        {editService ? 'Hizmet Düzenle' : 'Yeni Doküman Hizmeti'}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Student Selection (Read-only if editing) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Öğrenci
                            </label>
                            {editService ? (
                                <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                                    {selectedStudent?.first_name} {selectedStudent?.last_name}
                                </div>
                            ) : selectedStudent ? (
                                <div className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                    <span className="font-medium text-blue-900 dark:text-blue-100">
                                        {selectedStudent.first_name} {selectedStudent.last_name}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => { setSelectedStudent(null); setSearchTerm(''); }}
                                        className="text-blue-500 hover:text-blue-700"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder="Öğrenci ara..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500"
                                    />
                                    {searchResults.length > 0 && (
                                        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                                            {searchResults.map(student => (
                                                <button
                                                    key={student.id}
                                                    type="button"
                                                    onClick={() => { setSelectedStudent(student); setSearchTerm(''); }}
                                                    className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm text-gray-900 dark:text-white"
                                                >
                                                    {student.first_name} {student.last_name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Service Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Hizmet Türü (Birden fazla seçilebilir)
                            </label>
                            <div className="space-y-2">
                                {['cv', 'portfolio', 'motivation_letter'].map(type => (
                                    <label key={type} className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.service_type.includes(type)}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setFormData(prev => {
                                                    const currentTypes = prev.service_type;
                                                    if (checked) {
                                                        // If array, add. Note: Initial state must be array now.
                                                        return { ...prev, service_type: [...currentTypes, type] };
                                                    } else {
                                                        return { ...prev, service_type: currentTypes.filter(t => t !== type) };
                                                    }
                                                });
                                            }}
                                            className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                        />
                                        <span className="text-gray-900 dark:text-gray-300">
                                            {type === 'cv' && 'CV Hazırlama'}
                                            {type === 'portfolio' && 'Portfolyo'}
                                            {type === 'motivation_letter' && 'Motivasyon Mektubu'}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Status */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Durum
                            </label>
                            <select
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            >
                                <option value="brief_received">Brief Alındı</option>
                                <option value="draft_prepared">Taslak Hazırlandı</option>
                                <option value="revision_expected">Revizyon Bekleniyor</option>
                                <option value="completed">Tamamlandı</option>
                            </select>
                        </div>

                        {/* Deadline */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Teslim Tarihi
                            </label>
                            <input
                                type="date"
                                value={formData.deadline}
                                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                        </div>

                        {/* Payment Section */}
                        <div className="bg-blue-50 dark:bg-blue-900/10 p-4 rounded-xl space-y-4 border border-blue-100 dark:border-blue-900/20">
                            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 border-b border-blue-200 dark:border-blue-800 pb-2">
                                Ödeme Bilgileri
                            </h3>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Tutar</label>
                                    <input
                                        type="number"
                                        placeholder="0.00"
                                        value={formData.price}
                                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                        className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Para Birimi</label>
                                    <select
                                        value={formData.currency}
                                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    >
                                        <option value="TRY">TRY (₺)</option>
                                        <option value="EUR">EUR (€)</option>
                                        <option value="USD">USD ($)</option>
                                        <option value="GBP">GBP (£)</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Şirket</label>
                                    <select
                                        value={formData.company_entity}
                                        onChange={(e) => setFormData({ ...formData, company_entity: e.target.value })}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    >
                                        <option value="">Seçiniz</option>
                                        <option value="Pisa Edu">Pisa Edu</option>
                                        <option value="Pisa Test Prep">Pisa Test Prep</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Ödeme Yöntemi</label>
                                    <select
                                        value={formData.payment_method}
                                        onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                                        className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    >
                                        <option value="">Seçiniz</option>
                                        <optgroup label="Nakit">
                                            <option value="Nakit - Bireysel hesap">Bireysel hesap</option>
                                            <option value="Nakit - Skyland ofis">Skyland ofis</option>
                                            <option value="Nakit - Ankara ofis">Ankara ofis</option>
                                            <option value="Nakit - Bursa Ofis">Bursa Ofis</option>
                                            <option value="Nakit - İzmir Ofis">İzmir Ofis</option>
                                            <option value="Nakit - Kadıköy Ofis">Kadıköy Ofis</option>
                                            <option value="Nakit - Fethiye Ofis">Fethiye Ofis</option>
                                        </optgroup>
                                        <optgroup label="Kredi Kartı">
                                            <option value="Kredi Kartı - Pisa Edu Moka">Pisa Edu Moka</option>
                                            <option value="Kredi Kartı - Pisa Test Prep Moka">Pisa Test Prep Moka</option>
                                        </optgroup>
                                        <optgroup label="Şirket">
                                            <option value="Şirket - Pisa Edu">Pisa Edu</option>
                                            <option value="Şirket - Pisa Test Prep">Pisa Test Prep</option>
                                        </optgroup>
                                        <option value="havale">Havale/EFT</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex items-center">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_paid}
                                        onChange={(e) => setFormData({ ...formData, is_paid: e.target.checked })}
                                        className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                                    />
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Ödendi</span>
                                </label>
                            </div>
                        </div>

                        {/* Notes */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Notlar
                            </label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                rows={3}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                placeholder="Varsa ek notlar..."
                            />
                        </div>

                        <div className="flex justify-end pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg mr-2"
                            >
                                İptal
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center disabled:opacity-50"
                            >
                                <Save className="w-4 h-4 mr-2" />
                                {loading ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddDocumentServiceModal;
